import { Check, RotateCcw } from "lucide-react-native";
import { FC } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import HoverCard from "./HoverCard";
import { channelMessages } from "./messages";
import { FACTOR_GROUPS, FACTORS } from "./model";
import PackPreview from "./PackPreview";
import { Configuration, Factor, FactorId } from "./types";

/* ── Types ─────────────────────────────────────── */

interface ConfigurationPanelProps {
  audienceLabel: string;
  configuration: Configuration;
  /** Whether the current configuration differs from the audience optimum. */
  modified: boolean;
  onChange: (factor: FactorId, value: string) => void;
  onReset: () => void;
}

interface ChipProps {
  description?: string;
  label: string;
  onPress?: () => void;
  selected: boolean;
}

/* ── Constants ─────────────────────────────────── */

const PURPLE = "#7c3aed";
const PURPLE_BG = "#f5f3ff";

/* ── Component ─────────────────────────────────── */

/**
 * The launch configuration. Opens on the audience optimum; every knob can be
 * changed to see the impact on the results.
 */
const ConfigurationPanel: FC<ConfigurationPanelProps> = ({
  audienceLabel,
  configuration,
  modified,
  onChange,
  onReset,
}) => (
  <Card className="p-6 rounded-2xl border border-outline-200">
    <VStack className="gap-6">
      <HStack className="items-start justify-between gap-3">
        <VStack className="gap-0.5 flex-1">
          <Text className="text-base font-semibold text-typography-900">
            {modified ? "Configuration" : "Optimal configuration"}
          </Text>
          <Text className="text-xs text-typography-400">
            {modified
              ? `Edited from the optimum for ${audienceLabel}. Results update live.`
              : `Highest-scoring scenario for ${audienceLabel}. Change any option to test it.`}
          </Text>
        </VStack>
        {modified && (
          <Pressable
            className="flex-row items-center gap-1 shrink-0 pt-0.5"
            onPress={onReset}
          >
            <RotateCcw color={PURPLE} size={12} />
            <Text className="text-xs font-medium" style={{ color: PURPLE }}>
              Reset to optimal
            </Text>
          </Pressable>
        )}
      </HStack>

      <Box className="items-center -mt-2">
        <PackPreview configuration={configuration} gallery height={230} />
      </Box>

      {FACTOR_GROUPS.map((group) => (
        <VStack className="gap-3" key={group.id}>
          <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
            {group.label}
          </Text>
          <VStack className="gap-3">
            <VStack className="gap-3">
              {FACTORS.filter((f) => f.group === group.id).map((factor) =>
                factor.id === "packaging" ? (
                  <PackDesignScale
                    configuration={configuration}
                    factor={factor}
                    key={factor.id}
                    onChange={(value) => onChange(factor.id, value)}
                  />
                ) : (
                  <FactorRow
                    factor={factor}
                    key={factor.id}
                    onChange={(value) => onChange(factor.id, value)}
                    value={configuration[factor.id]}
                  />
                )
              )}
              {group.id === "goToMarket" && (
                <ChannelMessages configuration={configuration} />
              )}
            </VStack>
          </VStack>
        </VStack>
      ))}
    </VStack>
  </Card>
);

/* ── Subcomponents ─────────────────────────────── */

/** What each channel of the selected media plan says. */
const ChannelMessages: FC<{ configuration: Configuration }> = ({
  configuration,
}) => {
  const lines = channelMessages(configuration);
  if (lines.length === 0) return null;
  return (
    <VStack
      className="gap-1.5 rounded-lg p-3"
      style={{ backgroundColor: "#fafaf9" }}
    >
      <Text className="text-[11px] font-medium text-typography-500">
        Message per channel
      </Text>
      {lines.map((line) => (
        <Text
          className="text-[11px] leading-4 text-typography-700"
          key={line.channel}
        >
          <Text className="text-[11px] font-semibold text-typography-900">
            {line.channel}:{" "}
          </Text>
          {line.message}
        </Text>
      ))}
    </VStack>
  );
};

const Chip: FC<ChipProps> = ({ description, label, onPress, selected }) => {
  const chip = (
    <Pressable
      className={`flex-row items-center gap-1 px-2 py-0.5 rounded-full border ${
        selected
          ? "border-transparent"
          : onPress
            ? "border-outline-200 bg-background-0 hover:border-outline-400"
            : "border-outline-200 bg-background-0"
      }`}
      disabled={!onPress}
      onPress={onPress}
      style={selected ? { backgroundColor: PURPLE_BG } : undefined}
    >
      {selected && <Check color={PURPLE} size={11} strokeWidth={3} />}
      <Text
        className={`text-[11px] ${selected ? "font-semibold" : "text-typography-500"}`}
        style={selected ? { color: PURPLE } : undefined}
      >
        {label}
      </Text>
    </Pressable>
  );
  if (!description) return chip;
  return (
    <HoverCard
      content={
        <VStack className="gap-1">
          <Text className="text-xs font-semibold text-typography-900">
            {label}
          </Text>
          <Text className="text-xs leading-4 text-typography-600">
            {description}
          </Text>
        </VStack>
      }
      width={280}
    >
      {chip}
    </HoverCard>
  );
};

const FactorRow: FC<{
  factor: Factor;
  onChange: (value: string) => void;
  value: string;
}> = ({ factor, onChange, value }) => {
  if (factor.members) {
    return (
      <HStack className="items-start gap-3">
        <RowLabel label={factor.label} />
        <VStack className="flex-1 gap-1.5">
          {factor.options.map((option) => (
            <MemberOption
              factor={factor}
              key={option.value}
              members={option.members ?? []}
              onPress={() => onChange(option.value)}
              selected={option.value === value}
            />
          ))}
        </VStack>
      </HStack>
    );
  }
  return (
    <HStack className="items-start gap-3">
      <RowLabel label={factor.label} />
      <Box className="flex-1 flex-row flex-wrap gap-1.5">
        {factor.options.map((option) => (
          <Chip
            description={option.description}
            key={option.value}
            label={option.label}
            onPress={() => onChange(option.value)}
            selected={option.value === value}
          />
        ))}
      </Box>
    </HStack>
  );
};

/** One selectable combination of retailers / channels, shown as member chips. */
const MemberOption: FC<{
  factor: Factor;
  members: string[];
  onPress: () => void;
  selected: boolean;
}> = ({ factor, members, onPress, selected }) => (
  <Pressable
    className={`flex-row flex-wrap items-center gap-1 rounded-lg px-1.5 py-1 border ${
      selected
        ? "border-transparent"
        : "border-transparent hover:bg-background-50"
    }`}
    onPress={onPress}
    style={selected ? { backgroundColor: PURPLE_BG } : undefined}
  >
    <Box className="w-3.5 items-center">
      {selected && <Check color={PURPLE} size={11} strokeWidth={3} />}
    </Box>
    {members.map((id, index) => (
      <HStack className="items-center gap-1" key={id}>
        {index > 0 && (
          <Text className="text-[11px] text-typography-400">+</Text>
        )}
        <Text
          className={`text-[11px] ${selected ? "font-semibold" : "text-typography-500"}`}
          style={selected ? { color: PURPLE } : undefined}
        >
          {factor.members?.find((m) => m.id === id)?.label ?? id}
        </Text>
      </HStack>
    ))}
  </Pressable>
);

/** Five-step scale from a Nutella-only jar to a peanut-only jar. */
const PackDesignScale: FC<{
  configuration: Configuration;
  factor: Factor;
  onChange: (value: string) => void;
}> = ({ configuration, factor, onChange }) => {
  const index = factor.options.findIndex(
    (o) => o.value === configuration.packaging
  );
  const selected = factor.options[index];
  return (
    <HStack className="items-start gap-3">
      <RowLabel label={factor.label} />
      <VStack className="flex-1 gap-1.5">
        <HStack className="items-center gap-1">
          {factor.options.map((option, i) => (
            <HoverCard
              content={
                <HStack className="gap-3 items-center">
                  <PackPreview
                    configuration={configuration}
                    height={120}
                    packaging={option.value}
                  />
                  <VStack className="flex-1 gap-1">
                    <Text className="text-xs font-semibold text-typography-900">
                      {option.label}
                    </Text>
                    <Text className="text-xs leading-4 text-typography-600">
                      {option.description}
                    </Text>
                  </VStack>
                </HStack>
              }
              fill
              key={option.value}
              width={320}
            >
              <Pressable
                className="flex-1 items-center py-1"
                onPress={() => onChange(option.value)}
              >
                <Box
                  className="h-2 w-full rounded-full"
                  style={{
                    backgroundColor:
                      i <= index ? PURPLE : "rgb(var(--color-outline-200))",
                    opacity: i === index ? 1 : i < index ? 0.45 : 1,
                  }}
                />
              </Pressable>
            </HoverCard>
          ))}
        </HStack>
        <HStack className="items-center justify-between">
          <Text className="text-[11px] text-typography-400">Nutella</Text>
          <Text className="text-[11px] font-semibold" style={{ color: PURPLE }}>
            {selected?.label}
          </Text>
          <Text className="text-[11px] text-typography-400">Peanut</Text>
        </HStack>
      </VStack>
    </HStack>
  );
};

const RowLabel: FC<{ label: string }> = ({ label }) => (
  <Text className="w-20 shrink-0 pt-1 text-xs text-typography-500 leading-4">
    {label}
  </Text>
);

export default ConfigurationPanel;
